/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { inject, Injectable } from '@angular/core';
import { Resource, ResourceUri } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { SingleResourceStateService, StateService } from '../ngrx/state.service';
import { ResourceLoader } from './resource.loader';
import { ResourceServiceConfig } from './resource.model';
import { StateResource } from './resource.util';

/**
 * B = Type of baseresource
 * T = Type of the resource which is working on
 */
@Injectable()
export abstract class ResourceService<B extends Resource, T extends Resource> {
  private readonly stateService: StateService = inject(StateService);

  resourceStateService: SingleResourceStateService<T>;
  resourceLoader: ResourceLoader<B, T>;

  constructor(protected config: ResourceServiceConfig<B>) {
    this.initStateService();
    this.initResourceLoader();
  }

  private initStateService(): void {
    this.resourceStateService = this.stateService.createSingleResourceService(this.config.stateInfo);
  }

  private initResourceLoader(): void {
    this.resourceLoader = new ResourceLoader<B, T>(this.config, this.resourceStateService);
  }

  public get(): Observable<StateResource<T>> {
    return this.resourceLoader.get();
  }

  public reloadResource(): void {
    this.resourceStateService.reloadResource();
  }

  public existResource(): Observable<boolean> {
    return this.resourceStateService.existsResource();
  }

  public selectResource(): Observable<StateResource<T>> {
    return this.resourceStateService.selectResource();
  }

  public loadByResourceUri(resourceUri: ResourceUri): void {
    this.resourceStateService.loadResource(resourceUri);
  }

  public clearResource(): void {
    this.resourceStateService.clearResource();
  }

  public getConfig(): ResourceServiceConfig<B> {
    return this.config;
  }
}
